import {
  Component,
  inject,
  input,
  InputSignal,
  OnDestroy,
  OnInit,
  output,
  OutputEmitterRef,
  signal,
  WritableSignal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ControlContainer,
  FormGroupDirective,
  FormsModule,
  NgControl,
  ReactiveFormsModule,
} from '@angular/forms';
import { PaymentProviderStore } from '../../../../services/api/payment-provider/payment-provider-store.service';
import { FormInputComponent } from '../form-input.component';
import { AppendTestIdPipe } from '../../../../pipes/append-test-id/append-test-id.pipe';
import { ErrorMessageComponent } from '../../../layout/error-message/error-message.component';
import { fromEvent } from 'rxjs';
import { LoadingAnimationComponent } from '../../../feedback/loading-animation/loading-animation.component';
import { EEntityType, ERelationType, IPaymentProvider } from '@web/dtos';
import { FilterTextsPipe } from '../../../../pipes/filterTexts/filterTexts.pipe';
import { LanguagePipe } from '../../../../pipes/language/language.pipe';

@Component({
  selector: 'aw-payment-select',
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    AppendTestIdPipe,
    ErrorMessageComponent,
    LoadingAnimationComponent,
    FilterTextsPipe,
    LanguagePipe,
  ],
  templateUrl: './payment-select.component.html',
  viewProviders: [
    {
      provide: ControlContainer,
      useExisting: FormGroupDirective,
    },
  ],
  styles: ``,
})
export class PaymentSelectComponent
  extends FormInputComponent
  implements OnInit, OnDestroy
{
  paymentProvider: InputSignal<IPaymentProvider | null | undefined> =
    input<IPaymentProvider | null>();

  paymentProviderChange: OutputEmitterRef<IPaymentProvider> =
    output<IPaymentProvider>();

  paymentProviderStore: PaymentProviderStore = inject(PaymentProviderStore);

  protected readonly EEntityType: typeof EEntityType = EEntityType;
  protected readonly ERelationType: typeof ERelationType = ERelationType;

  public readonly selectedItem: WritableSignal<number | undefined> =
    signal(undefined);
  public readonly showList: WritableSignal<boolean> = signal(false);

  constructor(public override ngControl: NgControl) {
    super(ngControl);
  }

  ngOnInit() {
    this.subscription.add(
      fromEvent<MouseEvent & { target?: { id: string } }>(
        document,
        'click'
      ).subscribe(event => {
        const isCorrectTarget = event?.target?.id === this.id();
        if (!isCorrectTarget) {
          this.showList.set(false);
        } else if (isCorrectTarget && !this.showList()) {
          this.showList.set(true);
        }
      })
    );
    this.subscription.add(
      fromEvent<KeyboardEvent & { target?: { id: string } }>(
        document,
        'keyup'
      ).subscribe(event => {
        const options = this.paymentProviderStore.paymentProviders();
        const isCorrectTarget = event.target?.id === this.id();
        if (isCorrectTarget) {
          event.preventDefault();
          if (event.key === 'ArrowDown' && isCorrectTarget) {
            this.selectedItem.set(
              this.selectedItem() !== undefined
                ? this.clampSelectedItem(
                    this.selectedItem()! + 1,
                    options.length
                  )
                : 0
            );
          } else if (event.key === 'ArrowUp' && isCorrectTarget) {
            this.selectedItem.set(
              this.selectedItem() !== 0 && this.selectedItem() !== undefined
                ? this.clampSelectedItem(
                    this.selectedItem()! - 1,
                    options.length
                  )
                : undefined
            );
          } else if (
            event.key === 'Enter' &&
            isCorrectTarget &&
            this.selectedItem() !== undefined &&
            this.showList()
          ) {
            this.selectPaymentProvider(options?.[this.selectedItem()!]);
          }
        } else if (!isCorrectTarget && this.showList()) {
          event.preventDefault();
          this.showList.set(false);
        }
      })
    );
  }

  private clampSelectedItem(newValue: number, maxLength: number): number {
    if (newValue >= maxLength) {
      return maxLength - 1;
    } else if (newValue < 0) {
      return 0;
    }
    return newValue;
  }

  public selectPaymentProvider(option: IPaymentProvider): void {
    this.ngControl.control?.setValue(option);
    this.ngControl.control?.updateValueAndValidity();
    this.showList.set(false);
  }
}
